import { BackendChoiceSchema } from '@electron/shared/backend-choice.types';
import { getDeviceTimeZone } from '@electron/shared/device-timezone';
import { apiClient } from '@littlebird/auth-client';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import {
  JournalConfigSchema,
  JournalNotificationTime,
} from '@/features/journal/journal-queries';
import { logError } from '@/lib/errorLogging';
import { settingsStore } from '@/stores/settings.store';

const UserSchema = z
  .object({
    id: z.union([z.string(), z.number()]).pipe(z.coerce.string()),
    sub: z.string(),
    email: z.string(),
    feature_access: z.enum(['user', 'beta', 'dev', 'admin', 'also_admin']),
    global_prompt: z.string().nullable(),
    chat_derived_prompt: z.string().nullable(),
    info_to_ignore_prompt: z.string().nullable(),
    summary_prompt: z.string().nullable(),
    latest_input: z.string().nullable(),
    total_inputs: z.number(),
    name: z.string().nullable(),
    aliases: z.string().nullable(),
    avatar_url: z.string().nullable().optional(),
    is_onboarded_desktop: z.boolean().nullable().optional().default(null),
    is_onboarded_mobile: z.boolean().nullable().optional().default(null),
    is_onboarded_web: z.boolean().nullable().optional().default(null),
    // Deprecated: Use journal_config.notification_time instead
    journal_notification_time: z.nativeEnum(JournalNotificationTime).nullish(),
    journal_config: JournalConfigSchema.optional().nullable(),
    created_at: z.string().nullish(),
    feature_flags: z.record(z.string(), z.any()).optional(),
    is_welcome_page_eligible: z.boolean().optional().default(false),
    // Server-evaluated PostHog flags (bool, or variant string for multivariate
    // flags), for decisions made before the client PostHog SDK has loaded.
    // A missing key means "not evaluated" - fail closed.
    posthog_feature_flags: z
      .record(z.string(), z.union([z.boolean(), z.string()]))
      .optional()
      .default({}),
    client_version_supported: z.boolean().optional().default(true),
    calendar_connected: z.boolean().optional().default(false),
    custom_transcription_vocabulary: z.array(z.string()).optional().default([]),
    chat_mode_details: z
      .record(z.string(), z.object({ response_mode: z.string() }))
      .optional()
      .default({}),
    meeting_language_preference: z.string().optional().nullable(),
    timezone: z.string().nullable().optional(),
    backend_choice: BackendChoiceSchema,
  })
  .transform((data) => ({
    ...data,
    // Derive allowed_chat_modes from chat_mode_details keys
    allowed_chat_modes: Object.keys(data.chat_mode_details),
  }));

export type User = z.infer<typeof UserSchema>;

export const USER_QUERY_KEY = ['user'];

// The route loaders, the layout and the return-to-app handler all read the
// user at launch. Within this window they share one request. A change made
// elsewhere, such as a plan upgrade in the browser, shows on the first return
// to the app after the window passes
const USER_STALE_TIME_MS = 30_000;

export const userQueryOptions = (options?: { accessToken?: string }) =>
  queryOptions({
    queryKey: USER_QUERY_KEY,
    retry: 2,
    staleTime: USER_STALE_TIME_MS,
    queryFn: async () => {
      const headers: Record<string, string> = {};
      if (settingsStore.sendDeviceTimezone) {
        headers['TZ'] = getDeviceTimeZone();
      }
      if (options?.accessToken) {
        headers['Authorization'] = `Bearer ${options.accessToken}`;
      }

      return await apiClient
        .get('get_user_data', { headers })
        .then((response) => {
          return UserSchema.parse(response);
        })
        .catch((error) => {
          logError(error, 'Failed to fetch user data', {
            context: 'user-data-fetch',
            metadata: {
              endpoint: 'get_user_data',
              hasAccessToken: Boolean(options?.accessToken),
            },
          });

          if (
            error?.message?.includes('401') ||
            error?.message?.includes('Unauthorized')
          ) {
            throw new Error('Authentication failed. Please sign in again.');
          }

          throw new Error(
            `Unable to fetch user data: ${error?.message || 'Unknown error'}`
          );
        });
    },
  });

export const userMutationOptions = () => ({
  mutationKey: ['user'],
  mutationFn: async (
    data: Partial<User> & { reset_todo_filter_to_defaults?: boolean }
  ) => {
    await apiClient.post('update_user_data', data);
  },
});
